import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, FileText, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

const Support = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: "How do I submit a restroom review?",
      answer: "From the home page, select your overall rating, enter the restaurant name and address, adjust the cleanliness, supplies, and maintenance sliders, add any comments, and click Submit. You'll need to be logged in to submit a review."
    },
    {
      question: "Do I need an account to use The Loo Report?",
      answer: "You can browse reviews without an account, but you'll need to create a free account or sign in with Google to submit reviews."
    },
    {
      question: "How do I delete my account?",
      answer: "To delete your account and all associated data, please contact us at support@thelooreport.com with the subject line 'Account Deletion Request' and include the email address associated with your account."
    },
    {
      question: "What is the URGENT ALERT feature?",
      answer: "The URGENT ALERT feature allows you to anonymously notify a restaurant about restroom issues via an automated phone call. This helps restaurants address problems quickly."
    },
    {
      question: "How are restroom ratings calculated?",
      answer: "Each review includes ratings for cleanliness (1-10), supplies (1-10), and maintenance (1-10), plus an overall experience rating. The combined scores help other users understand the restroom quality."
    },
    {
      question: "Can I edit or delete my review?",
      answer: "Currently, reviews cannot be edited after submission. If you need to remove a review, please contact support with the restaurant name and date of your review."
    },
    {
      question: "Is my personal information shared publicly?",
      answer: "Your email address is never displayed publicly. Only your name (which you can make anonymous) and review content are visible to other users."
    },
    {
      question: "How do I report an inappropriate review?",
      answer: "If you see a review that violates our guidelines, please contact us at support@thelooreport.com with the restaurant name and details about the concern."
    }
  ];

  return (
    <div className="min-h-screen bg-yellow-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center text-white hover:text-yellow-200 mb-4">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold">Support Center</h1>
          <p className="text-red-100 mt-2">We're here to help!</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        
        {/* Contact Options */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Email Support</h2>
            <p className="text-gray-600 mb-4">Get help via email. We typically respond within 24-48 hours.</p>
            <a 
              href="mailto:support@thelooreport.com" 
              className="inline-block bg-red-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-red-700 transition-colors"
            >
              support@thelooreport.com
            </a>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Privacy Policy</h2>
            <p className="text-gray-600 mb-4">Learn how we collect, use, and protect your information.</p>
            <Link 
              to="/privacy"
              className="inline-block bg-yellow-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-yellow-600 transition-colors"
            >
              View Privacy Policy
            </Link>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center mb-6">
            <HelpCircle className="w-8 h-8 text-red-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-800">Frequently Asked Questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <span className="font-semibold text-gray-800">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="p-4 bg-white border-t border-gray-200">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Additional Help */}
        <div className="mt-8 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl shadow-lg p-8 text-center text-white">
          <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h2 className="text-2xl font-bold mb-2">Still Need Help?</h2>
          <p className="text-red-100 mb-6">
            Can't find what you're looking for? Our support team is ready to assist you.
          </p>
          <a 
            href="mailto:support@thelooreport.com?subject=Support Request"
            className="inline-block bg-white text-red-600 px-8 py-3 rounded-full font-semibold hover:bg-yellow-100 transition-colors"
          >
            Contact Support
          </a>
        </div>

      </div>
    </div>
  );
};

export default Support;
